import React, { useState, useEffect, useRef } from 'react';
import { 
  XMarkIcon, 
  MagnifyingGlassIcon, 
  ArrowPathIcon,
  ExclamationCircleIcon,
  ScaleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  UserIcon,
  CalendarIcon,
  BuildingLibraryIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import MarkdownRenderer from '../common/MarkdownRenderer';
import { apiRequest } from '../../utils/apiHelper';

const ZimLIISearchPanel = ({ results, onSearch, onClose, loading }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [docType, setDocType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedFilter, setExpandedFilter] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [downloadMenus, setDownloadMenus] = useState({});
  const [downloadInfo, setDownloadInfo] = useState({});
  const downloadMenuRef = useRef(null);
  
  // Close download menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
        setDownloadMenus({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleSearch = (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) return;
    
    // Prepare search parameters
    const searchParams = {
      query: searchQuery,
      jurisdiction: jurisdiction || undefined,
      doc_type: docType || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined
    };
    
    onSearch(searchParams);
  };
  
  const toggleItemExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  const handleDownloadDocument = async (item) => {
    try {
      // Get download options from backend
      const downloadOptions = await getDownloadOptions(item);
      
      if (downloadOptions && downloadOptions.formats && downloadOptions.formats.length > 0) {
        // If there's only one option, use it directly
        if (downloadOptions.formats.length === 1) {
          window.open(downloadOptions.formats[0].url, '_blank', 'noopener,noreferrer');
        } else {
          // Show download menu for multiple options
          toggleDownloadMenu(item.id);
        }
      } else {
        // Fallback to direct URL
        if (item.url) {
          window.open(item.url, '_blank', 'noopener,noreferrer');
        }
      }
    } catch (error) {
      console.error('Error getting download options:', error);
      // Fallback to direct URL
      if (item.url) {
        window.open(item.url, '_blank', 'noopener,noreferrer');
      }
    }
  };
  
  const getDownloadOptions = async (item) => {
    try {
      if (downloadInfo[item.id]) {
        return downloadInfo[item.id];
      }
      
      const response = await apiRequest('/api/research/queries/zimlii-download-info/', 'POST', item);
      setDownloadInfo(prev => ({
        ...prev,
        [item.id]: response
      }));
      return response;
    } catch (error) {
      console.error('Error fetching download options:', error);
      return null;
    }
  };
  
  const toggleDownloadMenu = (itemId) => {
    setDownloadMenus(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const renderHighlights = (item) => {
    if (!item.pages || !Array.isArray(item.pages)) return null;
    
    const highlights = [];
    item.pages.forEach((page, pageIndex) => {
      if (page.highlight && page.highlight['pages.body']) {
        page.highlight['pages.body'].forEach((highlight, highlightIndex) => {
          highlights.push(
            <div key={`${pageIndex}-${highlightIndex}`} className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border-l-4 border-yellow-400">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                Page {page.page_num} (Score: {page.score?.toFixed(1)})
              </div>
              <div 
                className="text-sm text-gray-700 dark:text-gray-300"
                dangerouslySetInnerHTML={{ __html: highlight }}
              />
            </div>
          );
        });
      }
    });
    
    return highlights.length > 0 ? (
      <div className="mt-3">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
          <StarIcon className="h-4 w-4 mr-1 text-yellow-500" />
          Relevant Excerpts
        </h4>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {highlights}
        </div>
      </div>
    ) : null;
  };
  
  const renderMetadata = (item) => {
    const metadata = [];
    
    // Jurisdiction chip (always show if available)
    if (item.jurisdiction) {
      metadata.push(
        <div key="jurisdiction" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
          <ScaleIcon className="h-3 w-3 mr-1 flex-shrink-0" />
          <span>{item.jurisdiction}</span>
        </div>
      );
    }
    
    // Source chip
    {
      const source = item.source || 'Legal repository';
      metadata.push(
        <div key="source" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
          <BuildingLibraryIcon className="h-3 w-3 mr-1 flex-shrink-0" />
          <span>{source}</span>
        </div>
      );
    }
    
    // Accessed date chip
    {
      const accessed = item.accessed_date || new Date().toISOString().slice(0, 10);
      metadata.push(
        <div key="accessed" className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
          <CalendarIcon className="h-3 w-3 mr-1 flex-shrink-0" />
          <span>Accessed: {accessed}</span>
        </div>
      );
    }

    if (item.authors && item.authors.length > 0) {
      metadata.push(
        <div key="authors" className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <UserIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">{item.authors.join(', ')}</span>
        </div>
      );
    }
    
    if (item.nature) {
      metadata.push(
        <div key="nature" className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <DocumentDuplicateIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          <span>{item.nature}</span>
        </div>
      );
    }
    
    if (item.date) {
      metadata.push(
        <div key="date" className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <CalendarIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          <span>{formatDate(item.date)}</span>
        </div>
      );
    }
    
    if (item.court) {
      metadata.push(
        <div key="court" className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <BuildingLibraryIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          <span>{item.court}</span>
        </div>
      );
    }
    
    if (item.pages && item.pages.length > 0) {
      const totalPages = Math.max(...item.pages.map(p => p.page_num || 0));
      if (totalPages > 0) {
        metadata.push(
          <div key="pages" className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <DocumentTextIcon className="h-4 w-4 mr-1 flex-shrink-0" />
            <span>{totalPages} page{totalPages !== 1 ? 's' : ''}</span>
          </div>
        );
      }
    }
    
    return metadata.length > 0 ? (
      <div className="mt-2 flex flex-wrap gap-3">
        {metadata}
      </div>
    ) : null;
  };
  
  const [copiedItemId, setCopiedItemId] = useState(null);
  
  const formatCitation = (item) => {
    const parts = [];
    if (item.title) parts.push(item.title);
    if (item.citation) parts.push(item.citation);
    if (item.court) parts.push(`Court: ${item.court}`);
    if (item.jurisdiction) parts.push(`Jurisdiction: ${item.jurisdiction}`);
    else if (item.nature) parts.push(`Type: ${item.nature}`);
    const source = item.source || 'Verified legal repository';
    parts.push(`Source: ${source}`);
    const accessed = new Date().toISOString().slice(0, 10);
    parts.push(`Accessed: ${accessed}`);
    if (item.url) parts.push(item.url);
    return parts.join('. ').replace(/[<>]/g, '');
  };

  const handleCopyCitation = (item) => {
    const text = formatCitation(item);
    navigator.clipboard.writeText(text).then(() => {
      setCopiedItemId(item.id);
      setTimeout(() => setCopiedItemId(null), 2000);
    }).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedItemId(item.id);
      setTimeout(() => setCopiedItemId(null), 2000);
    });
  };
  
  const renderActionButtons = (item) => {
    const itemDownloadInfo = downloadInfo[item.id];
    const showDownloadMenu = downloadMenus[item.id];
    
    return (
      <div className="mt-3 flex space-x-2 flex-wrap gap-y-2">
        {/* Copy citation */}
        <button
          onClick={() => handleCopyCitation(item)}
          className="inline-flex items-center px-3 py-1.5 border border-primary-300 dark:border-primary-600 shadow-sm text-xs font-medium rounded text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <DocumentDuplicateIcon className="h-3 w-3 mr-1" />
          {copiedItemId === item.id ? 'Copied!' : 'Copy citation'}
        </button>

        {/* Open source */}
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <EyeIcon className="h-3 w-3 mr-1" />
            Open source
          </a>
        )}

        {item.url && (
          <div className="relative">
            <button
              onClick={() => handleDownloadDocument(item)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
              Download
              {itemDownloadInfo && itemDownloadInfo.formats && itemDownloadInfo.formats.length > 1 && (
                <ChevronDownIcon className="h-3 w-3 ml-1" />
              )}
            </button>
            
            {/* Download options dropdown */}
            {showDownloadMenu && itemDownloadInfo && itemDownloadInfo.formats && (
              <div ref={downloadMenuRef} className="absolute z-10 mt-1 w-64 bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700">
                <div className="py-1">
                  {itemDownloadInfo.formats.map((format, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        window.open(format.url, '_blank', 'noopener,noreferrer');
                        toggleDownloadMenu(item.id);
                      }}
                      className="w-full text-left px-4 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <div className="font-medium">{format.label}</div>
                      <div className="text-gray-500 dark:text-gray-400">{format.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Quick access to popular sections */}
        {item.pages && item.pages.length > 0 && (
          <button
            onClick={() => {
              // Find the page with the highest score and jump to it
              const bestPage = item.pages.reduce((best, current) => 
                (current.score || 0) > (best.score || 0) ? current : best
              );
              if (item.url) {
                window.open(`${item.url}#page-${bestPage.page_num}`, '_blank', 'noopener,noreferrer');
              }
            }}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <StarIcon className="h-3 w-3 mr-1" />
            Best Match
          </button>
        )}
      </div>
    );
  };
  
  const renderResultContent = (item) => {
    const commonExpandedContent = expandedItems[item.id] ? (
      <>
        {renderHighlights(item)}
        {item.excerpt && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Description</h4>
            <div className="text-sm text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-2">
              <MarkdownRenderer content={item.excerpt} />
            </div>
          </div>
        )}
        {item.provisions && item.provisions.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Relevant Provisions</h4>
            <div className="space-y-1">
              {item.provisions.slice(0, 5).map((provision, index) => (
                <div key={index} className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded">
                  {provision}
                </div>
              ))}
              {item.provisions.length > 5 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  ... and {item.provisions.length - 5} more provisions
                </div>
              )}
            </div>
          </div>
        )}
        {item.topic_path_names && item.topic_path_names.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Topics</h4>
            <div className="flex flex-wrap gap-1">
              {item.topic_path_names.map((topic, index) => (
                <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                  {topic}
                </span>
              ))}
            </div>
          </div>
        )}
        {renderActionButtons(item)}
      </>
    ) : null;

    switch (item.type) {
      case 'case':
      case 'judgment':
        return (
          <div>
            <div className="text-base sm:text-sm font-semibold sm:font-medium text-gray-900 dark:text-white break-words">{item.title}</div>
            {item.citation && (
              <div className="text-sm font-semibold text-primary-600 dark:text-primary-400 mt-1">
                {item.citation}
              </div>
            )}
            {renderMetadata(item)}
            {item.case_number && (
              <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Case Number: {item.case_number}
              </div>
            )}
            {item.judges && item.judges.length > 0 && (
              <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Judge{item.judges.length > 1 ? 's' : ''}: {item.judges.join(', ')}
              </div>
            )}
            {item.outcome && (
              <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Outcome: {item.outcome}
              </div>
            )}
            {commonExpandedContent}
          </div>
        );
        
      case 'legislation':
      case 'act':
        return (
          <div>
            <div className="text-base sm:text-sm font-semibold sm:font-medium text-gray-900 dark:text-white break-words">{item.title}</div>
            {renderMetadata(item)}
            {item.citation && (
              <div className="mt-1 text-sm font-semibold text-primary-600 dark:text-primary-400">
                {item.citation}
              </div>
            )}
            {commonExpandedContent}
          </div>
        );
        
      default:
        return (
          <div>
            <div className="text-base sm:text-sm font-semibold sm:font-medium text-gray-900 dark:text-white break-words">{item.title}</div>
            {renderMetadata(item)}
            {commonExpandedContent}
          </div>
        );
    }
  };
  
  const renderResultIcon = (item) => {
    switch (item.type) {
      case 'case':
        return <ScaleIcon className="h-5 w-5 text-blue-500" />;
      case 'legislation':
        return <DocumentTextIcon className="h-5 w-5 text-green-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };
  
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
            <ScaleIcon className="h-5 w-5 mr-2 text-primary-500" />
            Jurisdiction Search
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            title="Close panel"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* Search form */}
      <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <form onSubmit={handleSearch}>
          <div className="flex">
            <div className="flex-grow">
              <label htmlFor="search-query" className="sr-only">Search Query</label>
              <input
                type="text"
                id="search-query"
                placeholder="Search legal information across jurisdictions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-l-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={!searchQuery.trim() || loading}
              aria-label="Search"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-r-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {loading ? (
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <MagnifyingGlassIcon className="h-5 w-5 sm:mr-2" />
                  <span className="hidden sm:inline">Search</span>
                </>
              )}
            </button>
          </div>
          
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setExpandedFilter(!expandedFilter)}
              className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              {expandedFilter ? (
                <>
                  <ChevronUpIcon className="h-4 w-4 mr-1" />
                  Hide Filters
                </>
              ) : (
                <>
                  <ChevronDownIcon className="h-4 w-4 mr-1" />
                  Show Filters
                </>
              )}
            </button>
          </div>
          
          {expandedFilter && (
            <div className="mt-3 space-y-3">
              <div>
                <label htmlFor="jurisdiction" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Jurisdiction
                </label>
                <select
                  id="jurisdiction"
                  value={jurisdiction}
                  onChange={(e) => setJurisdiction(e.target.value)}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="">All Jurisdictions</option>
                  <optgroup label="Zimbabwe Courts">
                    <option value="supreme">Supreme Court</option>
                    <option value="constitutional">Constitutional Court</option>
                    <option value="high">High Court</option>
                    <option value="labour">Labour Court</option>
                    <option value="administrative">Administrative Court</option>
                  </optgroup>
                  <optgroup label="African Jurisdictions">
                    <option value="south-africa">South Africa</option>
                    <option value="kenya">Kenya</option>
                    <option value="ghana">Ghana</option>
                    <option value="nigeria">Nigeria</option>
                    <option value="uganda">Uganda</option>
                    <option value="tanzania">Tanzania</option>
                    <option value="zambia">Zambia</option>
                    <option value="malawi">Malawi</option>
                    <option value="namibia">Namibia</option>
                    <option value="rwanda">Rwanda</option>
                    <option value="mauritius">Mauritius</option>
                    <option value="lesotho">Lesotho</option>
                    <option value="eswatini">Eswatini</option>
                    <option value="sierra-leone">Sierra Leone</option>
                    <option value="seychelles">Seychelles</option>
                    <option value="liberia">Liberia</option>
                  </optgroup>
                </select>
              </div>
              
              <div>
                <label htmlFor="doc-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Document Type
                </label>
                <select
                  id="doc-type"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="">All Document Types</option>
                  <option value="case">Case Law</option>
                  <option value="legislation">Legislation</option>
                  <option value="constitution">Constitutional Documents</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="date-from" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    From Date
                  </label>
                  <input
                    type="date"
                    id="date-from"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="date-to" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    To Date
                  </label>
                  <input
                    type="date"
                    id="date-to"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
      
      {/* Results */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4">
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : results ? (
          <>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                {results.items?.length || 0} results found
              </h3>
              {results.query && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Search: "{results.query}"
                </span>
              )}
            </div>
            
            {results.error ? (
              <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-3">
                <div className="flex">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                  <div className="ml-3">
                    <p className="text-sm text-red-700 dark:text-red-300">{results.error}</p>
                  </div>
                </div>
              </div>
            ) : results.items?.length === 0 ? (
              <div className="text-center py-8">
                <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No results found</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Try adjusting your search or filters to find what you're looking for.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {(results.items || []).map((item) => (
                  <div
                    key={item.id}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3"
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-0.5 mr-2 sm:mr-3">
                        {renderResultIcon(item)}
                      </div>
                      <div className="flex-1 min-w-0">
                        {renderResultContent(item)}
                        {renderHighlights(item)}
                        {renderMetadata(item)}
                        {renderActionButtons(item)}
                      </div>
                      <div className="ml-2 sm:ml-3 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => toggleItemExpand(item.id)}
                          className="p-1.5 -m-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          aria-label={expandedItems[item.id] ? 'Collapse details' : 'Expand details'}
                        >
                          {expandedItems[item.id] ? (
                            <ChevronUpIcon className="h-5 w-5" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <ScaleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No search performed</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Search legal information across African jurisdictions using the form above.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZimLIISearchPanel;
